import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, CheckCircle, Award, Users, Target, TrendingUp, Shield, Zap } from 'lucide-react';
import { mockData } from '../mock/consultingData';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const AboutSection = () => {
  const { consultant } = mockData;
  
  const expertiseAreas = [
    { icon: Award, title: "Six Sigma Excellence", value: "2 BB, 5 GB Projects" },
    { icon: Users, title: "Team Development", value: "500+ Professionals Trained" },
    { icon: Target, title: "Project Success", value: "60+ Projects Led" },
    { icon: Zap, title: "Generative & Agentic AI", value: "AI Strategy & Implementation" },
    { icon: Shield, title: "ISO & Compliance", value: "International Standards" }
  ];
  
  // Scroll animation refs for different sections
  const headerRef = useScrollAnimation();
  const contentRef = useScrollAnimation();
  const expertiseRef = useScrollAnimation();
  const industryRef = useScrollAnimation();
  const ctaRef = useScrollAnimation();
  
  return (
    <section className="dark-section-alt" id="about" style={{ paddingBottom: '20px' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div 
          ref={headerRef}
          className="text-center mb-16 float-on-scroll"
        >
          <h2 className="display-medium mb-6">
            Meet Your <span className="turquoise-text">Transformation</span> Expert
          </h2>
          <p className="body-large max-w-3xl mx-auto text-gray-300">
            21+ years of proven expertise in driving operational excellence, implementing AI management systems, 
            and delivering measurable results across diverse industries.
          </p>
        </div>

        <div 
          ref={contentRef}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-16 px-4 lg:px-0 float-on-scroll"
        >
          {/* Left side - Content */}
          <div>
            <div className="mb-8">
              <h3 className="display-medium mb-4">{consultant.name}</h3>
              <p className="heading-2 turquoise-text mb-4">{consultant.title}</p>
              <p className="body-large mb-6 text-gray-300">{consultant.bio}</p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="bg-turquoise-500/10 text-turquoise-400 px-4 py-2 border border-turquoise-500/30">
                  <span className="body-medium font-medium">{consultant.experience}</span>
                </div>
                <div className="bg-gray-700/50 text-gray-300 px-4 py-2 border border-gray-600">
                  <span className="body-medium">Multi-Industry Expert</span>
                </div>
              </div>
            </div>
            
            <div className="mb-8">
              <h4 className="heading-3 mb-6">Key Achievements & Certifications</h4>
              <div className="grid grid-cols-1 gap-3">
                {consultant.achievements.map((achievement, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-turquoise-400 flex-shrink-0 mt-1" />
                    <span className="body-medium text-gray-300">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <Button 
              className="btn-primary"
              onClick={() => window.open('https://shanso-marketing.preview.emergentagent.com/', '_blank')}
            >
              <Award size={20} />
              View Full Portfolio
              <ArrowRight size={20} />
            </Button>
          </div>
          
          {/* Right side - Expertise Image */}
          <div className="relative group w-full max-h-[400px] lg:max-h-none overflow-hidden">
            <img 
              src="/transformation-expert.png"
              alt="Transformation Expert"
              className="w-full h-full object-cover rounded-xl transform group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        </div>

        {/* Expertise Cards Section - Below the main grid */}
        <div 
          ref={expertiseRef}
          className="mb-16 float-on-scroll"
        >
          <h4 className="heading-3 text-center mb-8">Core <span className="turquoise-text">Expertise</span> Areas</h4>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Side - Creative Image Section */}
            <div className="lg:col-span-4 hidden lg:block">
              <div className="sticky top-8">
                {/* Main Image Container with Creative Styling */}
                <div className="relative group">
                  {/* Multi-color Glow Effect Background */}
                  <div className="absolute -inset-4 bg-gradient-to-br from-turquoise-500/20 via-cyan-500/20 to-blue-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                  
                  {/* Image Container */}
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900/50 to-gray-800/50 p-1">
                    <img 
                      src="/core-expertise.png"
                      alt="Core Expertise - Six Sigma, Lean, ISO, Risk Management"
                      className="w-full h-auto rounded-xl transform group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - All 5 Expertise Cards */}
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {expertiseAreas.map((area, index) => (
                <div 
                  key={index} 
                  className="dark-card text-center p-3 hover:border-turquoise-500 transition-all duration-300"
                >
                  <div className="icon-container mx-auto mb-2 w-10 h-10">
                    <area.icon size={20} />
                  </div>
                  <h5 className="text-base font-semibold mb-1 text-gray-200">{area.title}</h5>
                  <p className="text-sm turquoise-text">{area.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Industry Experience - Creative Redesign */}
        <div 
          ref={industryRef}
          className="border-t border-gray-800 pt-16 float-on-scroll"
        >
          <h3 className="heading-2 text-center mb-16">Industry <span className="turquoise-text">Experience</span></h3>
          
          {/* Industry Experience with Image Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Side - Industry Cards in Creative Layout */}
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { 
                  name: "Banking & Financial Services", 
                  icon: "🏦", 
                  years: "3", 
                  gradient: "bg-gradient-to-br from-blue-500/10 to-turquoise-500/10"
                },
                { 
                  name: "Technology Services", 
                  icon: "💻", 
                  years: "3", 
                  gradient: "bg-gradient-to-br from-purple-500/10 to-turquoise-500/10"
                },
                { 
                  name: "Title Insurance", 
                  icon: "🏠", 
                  years: "3", 
                  gradient: "bg-gradient-to-br from-green-500/10 to-turquoise-500/10"
                },
                { 
                  name: "Supply Chain", 
                  icon: "🚚", 
                  years: "3", 
                  gradient: "bg-gradient-to-br from-orange-500/10 to-turquoise-500/10"
                },
                { 
                  name: "Market Research", 
                  icon: "📊", 
                  years: "5", 
                  gradient: "bg-gradient-to-br from-red-500/10 to-turquoise-500/10"
                },
                { 
                  name: "Advertising & Creative Production", 
                  icon: "🎨", 
                  years: "1", 
                  gradient: "bg-gradient-to-br from-pink-500/10 to-turquoise-500/10"
                },
                { 
                  name: "Patent Indexing", 
                  icon: "📋", 
                  years: "3", 
                  gradient: "bg-gradient-to-br from-indigo-500/10 to-turquoise-500/10"
                }
              ].map((industry, index) => (
                <div key={index} className={`industry-experience-card ${industry.gradient} group hover:scale-105 transition-all duration-300`}>
                  <div className="card-glow"></div>
                  <div className="card-content">
                    <div className="industry-icon-container">
                      <span className="industry-icon">{industry.icon}</span>
                      <div className="icon-pulse"></div>
                    </div>
                    <div className="industry-info">
                      <h4 className="industry-name">{industry.name}</h4>
                      <div className="years-badge">
                        <span className="years-number">{industry.years}</span>
                        <span className="years-text">Year{industry.years !== '1' ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </div>

            {/* Right Side - Creative Image Section */}
            <div className="lg:col-span-4 hidden lg:block">
              <div className="sticky top-8">
                {/* Main Image Container with Creative Styling */}
                <div className="relative group">
                  {/* Multi-color Glow Effect Background */}
                  <div className="absolute -inset-4 bg-gradient-to-bl from-purple-500/20 via-pink-500/20 to-orange-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                  
                  {/* Image Container */}
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900/50 to-gray-800/50 p-1">
                    <img 
                      src="/industry-experience.png"
                      alt="Industry Experience - Global Business Network"
                      className="w-full h-auto rounded-xl transform group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>

                {/* Stats Cards Below Image */}
                <div className="mt-6 space-y-3">
                  <div className="bg-gradient-to-r from-blue-500/10 to-turquoise-500/10 border border-blue-500/30 rounded-xl p-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-turquoise-400 mb-1">7+</div>
                      <p className="text-sm text-gray-400">Industries Served</p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-400 mb-1">21+</div>
                      <p className="text-sm text-gray-400">Years Experience</p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-orange-400 mb-1">60+</div>
                      <p className="text-sm text-gray-400">Projects Completed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action - Creative Layout with Image */}
        <div 
          ref={ctaRef}
          className="mt-16 bg-gradient-to-r from-turquoise-500/5 via-turquoise-500/10 to-turquoise-500/5 border border-turquoise-500/20 float-on-scroll"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 lg:p-12">
            {/* Left side - Excellence Image with Glow */}
            <div className="lg:col-span-5 flex items-center justify-center lg:justify-start">
              <div className="relative group w-full max-w-md">
                {/* Multi-color Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-turquoise-500/30 via-purple-500/20 to-pink-500/20 blur-2xl opacity-50 group-hover:opacity-70 transition-opacity duration-500"></div>
                
                {/* Image */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_aiconsult-frontend/artifacts/v79rvtpo_READY%20TO%20DRIVE%20EXCELLENCE.png"
                  alt="Ready to Drive Excellence"
                  className="relative w-full h-auto object-contain transform group-hover:scale-105 transition-transform duration-500"
                  style={{ filter: 'drop-shadow(0 0 20px rgba(20, 184, 166, 0.3))' }}
                />
              </div>
            </div>

            {/* Right side - CTA Content */}
            <div className="lg:col-span-7 flex flex-col justify-center text-center lg:text-left">
              <h3 className="heading-2 mb-4">Ready to Drive Excellence in Your Organization?</h3>
              <p className="body-large mb-8 text-gray-300">
                Let's discuss how my proven methodologies can transform your operations and deliver measurable results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  className="btn-primary"
                  onClick={() => window.location.href = 'tel:+919962536379'}
                >
                  Schedule Strategy Call
                </Button>
                <Button className="btn-secondary">
                  View Case Studies
                  <ArrowRight size={20} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;